import { rpx } from 'client/lib/rpx-client';
import { PageTitle } from '@course/components/headings';
import { GuideProductPage } from '@course/components/guide-course-page';
import { FormGroup } from '@components/async-form';
import { NewSignupsNotificationSettings } from '../guide-course-settings/notification-settings';
import { useMemo } from 'preact/hooks';
import { serialAsync } from 'client/utils/serial-async';
import { showError } from '@components/app-error';
import { debounce } from 'client/utils/debounce';
import { CourseProfileFieldsForm } from '@course/components/manage-course-profile-fields';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { COURSE_STUDENT_LIMIT } from 'shared/consts';
import { Toggle } from '@components/toggle';

export const route = defCoursesRoute({
  load,
  Page,
  authLevel: 'guide',
});

async function load(route: RouteLoadProps) {
  const { courseId } = route.params;
  const [product, settings] = await Promise.all([
    rpx.courses.getGuideCourse({ id: courseId }),
    rpx.courseSettings.getNotificationsSettings({ courseId }),
  ]);
  return {
    product,
    subscription: settings.enrollmentEmailSubscription,
  };
}

function Page(props: LoadedProps<typeof load>) {
  const { product, subscription } = props.state;

  const saveSubscription = useMemo(
    () =>
      serialAsync(async (subscription: typeof props.state.subscription) => {
        try {
          props.setState((s) => ({ ...s, subscription }));
          await rpx.courseSettings.setNotificationSettings({
            subscription,
            courseId: product.id,
            sendOrientationEmail: false,
            receiveAssessmentEmails: false,
            sendEnrollmentEmailToFacilitators: false,
          });
        } catch (err) {
          showError(err);
        }
      }),
    [],
  );

  const saveMaxMemberships = useMemo(
    () =>
      debounce(
        serialAsync(async (maxMemberships: number, isMembershipLimited: boolean) => {
          try {
            await rpx.courseSettings.setCourseMembershipLimits({
              courseId: product.id,
              maxMemberships: maxMemberships || COURSE_STUDENT_LIMIT,
              isMembershipLimited: isMembershipLimited,
            });
          } catch (err) {
            showError(err);
          }
        }),
      ),
    [],
  );

  const setMaxMemberships = (maxMemberships: string) => {
    const value = maxMemberships === '' ? 0 : parseInt(maxMemberships, 10);
    if (!isNaN(value)) {
      props.setState((s) => ({ ...s, product: { ...s.product, maxMemberships: value } }));
      saveMaxMemberships(value, product.isMembershipLimited);
    }
  };

  const setMembershipLimited = () => {
    const isMembershipLimited = !product.isMembershipLimited;
    props.setState((s) => ({
      ...s,
      product: { ...s.product, isMembershipLimited: isMembershipLimited },
    }));
    saveMaxMemberships(
      isMembershipLimited ? product.maxMemberships : COURSE_STUDENT_LIMIT,
      isMembershipLimited,
    );
  };

  return (
    <GuideProductPage page="settings" product={product}>
      <section class="px-4 p-8 md:p-20 md:px-8 flex flex-col gap-4 w-4xl max-w-full mx-auto divide-y">
        <PageTitle>Settings</PageTitle>
        <FormGroup prop="maxMemberships" class="py-6">
          <label class="grid sm:grid-cols-2 gap-8">
            <span class="flex flex-col">
              <strong>Limit</strong>
              <span class="text-gray-500">Limit the number of purchases.</span>
            </span>
            <span class="flex items-center">
              <Toggle
                class=""
                checked={product.isMembershipLimited}
                onClick={setMembershipLimited}
              />
            </span>
          </label>

          {product.isMembershipLimited && (
            <label class="grid sm:grid-cols-2 gap-8 pt-6">
              <span class="flex flex-col">
                <span class="text-gray-500">Enter a number for maximum number of purchases</span>
              </span>
              <span class="flex items-center">
                <input
                  class="inline-ruz-input w-full"
                  type="text"
                  name="maxMemberships"
                  placeholder="Unlimited"
                  value={product.maxMemberships}
                  onInput={(e: any) => setMaxMemberships(e.target.value)}
                />
              </span>
            </label>
          )}
        </FormGroup>
        <FormGroup prop="maxMemberships" class="py-6">
          <div class="grid sm:grid-cols-2 gap-8">
            <span class="flex flex-col">
              <strong>New signup notifications</strong>
              <span class="text-gray-500">Get notified when someone signs up.</span>
            </span>
            <section class="flex flex-col gap-2">
              <NewSignupsNotificationSettings
                subscription={subscription}
                setSubscription={saveSubscription}
              />
            </section>
          </div>
        </FormGroup>

        <FormGroup prop="maxMemberships" class="pt-6">
          <div class="grid sm:grid-cols-2 gap-8">
            <span class="flex flex-col">
              <strong>Profile fields</strong>
              <span class="text-gray-500">
                Select profile fields would you like your customers to fill out.
              </span>
            </span>
            <section class="border rounded-md p-4 flex flex-col gap-4">
              <strong>Available fields</strong>
              <CourseProfileFieldsForm courseId={product.id} />
            </section>
          </div>
        </FormGroup>
      </section>
    </GuideProductPage>
  );
}
